import React, { useState, useMemo, useCallback, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import useSettingEvalItems from "../../../../hooks/useSettingEvalItems";
import { useUIContext } from "../../../../context/UIContext";
import { useTranslation } from "react-i18next";
import { arrangeDataByHierarchy } from "../../../../util/specialCase";
import styles from "../css/EvalPlanDrawer.module.css";

export default function EvalPlanDrawer({
  lessonInfo,
  evalItemsData,
  onClose,
  getEvalItemsByLessonId,
  setEvalItemsData,
  schoolId,
}) {
  const { t } = useTranslation();
  const { updateEvalItems } = useSettingEvalItems();
  const { handleToastCenterTop } = useUIContext();
  const [isSaving, setIsSaving] = useState(false);
  const [evalPlanData, setEvalPlanData] = useState({});
  const printRef = useRef(null);

  // 최종 level(leaf node) 항목 찾기 - LessonStudentsItemsTable.jsx 로직 참고
  const findFinalLevelItems = useMemo(() => {
    if (!evalItemsData || evalItemsData.length === 0) return [];

    const findItems = (parentId, level) =>
      evalItemsData.filter(
        (item) => item.hierarchy_level === level && item.parent_id === parentId
      );

    const finalItems = [];
    const level1Items = findItems(0, 1);

    level1Items.forEach((level1) => {
      const level2Items = findItems(level1.id, 2);

      if (level2Items.length === 0) {
        // level 1만 있고 level 2가 없으면 → level 1이 최종
        finalItems.push({
          finalItem: level1,
          parentItems: [],
        });
      } else {
        level2Items.forEach((level2) => {
          const level3Items = findItems(level2.id, 3);

          if (level3Items.length === 0) {
            // level 1, 2가 있고 level 3이 없으면 → level 2가 최종
            finalItems.push({
              finalItem: level2,
              parentItems: [level1],
            });
          } else {
            // level 1, 2, 3이 모두 있으면 → level 3이 최종
            level3Items.forEach((level3) => {
              finalItems.push({
                finalItem: level3,
                parentItems: [level1, level2],
              });
            });
          }
        });
      }
    });

    return finalItems;
  }, [evalItemsData]);

  // textarea 자동 높이 조절
  const autoResize = useCallback((element) => {
    if (!element) return;
    element.style.height = "auto";
    element.style.height = element.scrollHeight + "px";
  }, []);

  // 초기 데이터 로드
  React.useEffect(() => {
    const initialData = {};
    findFinalLevelItems.forEach(({ finalItem }) => {
      initialData[finalItem.id] = {
        elements: finalItem.extra_data?.elements || "",
        criteria: finalItem.extra_data?.criteria || "",
      };
    });
    setEvalPlanData(initialData);
  }, [findFinalLevelItems]);

  // 컴포넌트 마운트 후 모든 textarea 높이 조절
  React.useEffect(() => {
    const textareas = document.querySelectorAll(
      `.${styles.autoResizeTextarea}`
    );
    textareas.forEach((textarea) => autoResize(textarea));
  }, [evalPlanData, autoResize]);

  // 입력 필드 변경 핸들러
  const handleChange = useCallback(
    (itemId, field, value, e) => {
      setEvalPlanData((prev) => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          [field]: value,
        },
      }));
      if (e?.target) {
        autoResize(e.target);
      }
    },
    [autoResize]
  );

  // 저장 핸들러
  const handleSave = async () => {
    setIsSaving(true);

    // 변경사항이 있는 항목만 필터링
    const itemsToUpdate = findFinalLevelItems
      .map(({ finalItem }) => {
        const currentData = evalPlanData[finalItem.id];
        const originalElements = finalItem.extra_data?.elements || "";
        const originalCriteria = finalItem.extra_data?.criteria || "";

        // 변경사항이 있는지 확인
        if (
          currentData?.elements !== originalElements ||
          currentData?.criteria !== originalCriteria
        ) {
          return {
            ...finalItem,
            extra_data: {
              ...finalItem.extra_data,
              elements: currentData?.elements || "",
              criteria: currentData?.criteria || "",
            },
          };
        }
        return null;
      })
      .filter((item) => item !== null);

    // 변경사항이 없으면 저장하지 않음
    if (itemsToUpdate.length === 0) {
      setIsSaving(false);
      handleToastCenterTop(
        t("common.messages.notModifed") || "변경사항이 없습니다."
      );
      return;
    }

    try {
      // 모든 업데이트를 병렬로 처리
      const updatePromises = itemsToUpdate.map((updatedItem) =>
        updateEvalItems.mutateAsync(updatedItem)
      );

      await Promise.all(updatePromises);

      // 데이터 새로고침
      const forWhere = {
        schoolid: schoolId,
        lesson_id: lessonInfo.id,
      };

      getEvalItemsByLessonId.mutate(forWhere, {
        onSuccess: (data) => {
          const processedData = arrangeDataByHierarchy([...data]);
          setEvalItemsData(processedData);

          handleToastCenterTop(
            t("common.messages.savedSuccessfully") ||
              "성공적으로 저장되었습니다.",
            2000,
            "#28a745"
          );
          setIsSaving(false);
        },
        onError: (error) => {
          console.error(error);
          setIsSaving(false);
          handleToastCenterTop(t("common.messages.errorAlert") + error.message);
        },
      });
    } catch (error) {
      console.error("저장 실패:", error);
      setIsSaving(false);
      handleToastCenterTop(
        t("common.messages.errorAlert") +
          (error.message || "저장에 실패했습니다.")
      );
    }
  };

  // 수업 정보 텍스트
  const targetText = useMemo(() => {
    if (!lessonInfo) return "";
    const parts = [];
    if (lessonInfo.hierarchyTwo) parts.push(lessonInfo.hierarchyTwo);
    if (lessonInfo.hierarchyThree) parts.push(lessonInfo.hierarchyThree);
    return parts.join("-") || "전체";
  }, [lessonInfo]);

  const teachersText = useMemo(() => {
    if (!lessonInfo?.teachers) return "";
    return lessonInfo.teachers.map((t) => t.tname).join(", ");
  }, [lessonInfo]);

  // 학년도 표시 텍스트
  const schoolYearText = useMemo(() => {
    if (!lessonInfo) return "";
    return lessonInfo.school_year || "";
  }, [lessonInfo]);

  // 학기 표시 텍스트
  const termText = useMemo(() => {
    if (!lessonInfo) return "";
    return lessonInfo.termName + lessonInfo?.TermDates?.term_category || "";
  }, [lessonInfo]);

  // 인쇄
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `${schoolYearText}-${termText}-${
      lessonInfo?.subject_name || "평가계획서"
    }`,
    pageStyle: `
      @media print {
        @page {
          size: A4 portrait;
          margin: 15mm;
        }
        .no-print {
          display: none !important;
        }
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `,
  });

  // 계층 구조 렌더링을 위한 헬퍼 함수
  const findItems = useCallback(
    (parentId, level) =>
      evalItemsData.filter(
        (item) => item.hierarchy_level === level && item.parent_id === parentId
      ),
    [evalItemsData]
  );

  const findByLevels = useCallback(
    (level) => evalItemsData.filter((item) => item.hierarchy_level === level),
    [evalItemsData]
  );

  // 계층 구조 테이블 행 렌더링
  const renderTableRows = useCallback(() => {
    if (!evalItemsData || evalItemsData.length === 0) {
      return (
        <tr>
          <td colSpan={3} className="text-center" style={{ padding: "20px" }}>
            {t("teacher.lessonRelated.noEvalItems") || "평가 항목이 없습니다."}
          </td>
        </tr>
      );
    }

    const level1Items = findItems(0, 1);
    // 테이블 전체에 level3가 있는지 확인
    const hasLevel3InTable = findByLevels(3).length > 0;

    return level1Items.flatMap((level1) => {
      const level2Items = findItems(level1.id, 2);

      // Level2가 없는 경우 (Level1만 있는 경우)
      if (level2Items.length === 0) {
        const finalItemData = evalPlanData[level1.id] || {
          elements: "",
          criteria: "",
        };

        return (
          <tr key={`level1-${level1.id}`}>
            <td colSpan={1} className={styles.evalItemCell}>
              <div style={{ fontWeight: "500" }}>{level1.content}</div>
              {level1.extra_data?.weight && (
                <div>{level1.extra_data.weight}%</div>
              )}
              {level1.extra_data?.total && (
                <div>만점:{level1.extra_data.total}</div>
              )}
            </td>
            <td className={styles.evalElementsCell}>
              <textarea
                className={`${styles.autoResizeTextarea} ${styles.evalTextarea}`}
                value={finalItemData.elements}
                onChange={(e) =>
                  handleChange(level1.id, "elements", e.target.value, e)
                }
                rows={1}
              />
            </td>
            <td className={styles.evalCriteriaCell}>
              <textarea
                className={`${styles.autoResizeTextarea} ${styles.evalTextarea}`}
                value={finalItemData.criteria}
                onChange={(e) =>
                  handleChange(level1.id, "criteria", e.target.value, e)
                }
                rows={1}
              />
            </td>
          </tr>
        );
      } else {
        // Level2가 있는 경우
        const level1RowSpan = level2Items.reduce((total, level2) => {
          const level3Items = findItems(level2.id, 3);
          return total + Math.max(1, level3Items.length);
        }, 0);

        return level2Items.flatMap((level2, level2Index) => {
          const level3Items = findItems(level2.id, 3);

          // Level3가 없는 경우 (Level2까지만 있는 경우)
          if (level3Items.length === 0) {
            const finalItemData = evalPlanData[level2.id] || {
              elements: "",
              criteria: "",
            };

            // 테이블 전체에 level3가 있으면 colspan 2, 없으면 colspan 1
            const level2ColSpan = hasLevel3InTable ? 2 : 1;

            return (
              <tr key={`level2-${level2.id}-${level2Index}`}>
                {level2Index === 0 && (
                  <td
                    rowSpan={level1RowSpan}
                    colSpan={1}
                    className={styles.evalItemCell}
                  >
                    <div style={{ fontWeight: "500" }}>{level1.content}</div>
                    {level1.extra_data?.weight && (
                      <div>{level1.extra_data.weight}%</div>
                    )}
                    {level1.extra_data?.total && (
                      <div>만점:{level1.extra_data.total}</div>
                    )}
                  </td>
                )}
                <td colSpan={level2ColSpan} className={styles.evalItemCell}>
                  <div style={{ fontWeight: "500" }}>{level2.content}</div>
                  {level2.extra_data?.weight && (
                    <div>{level2.extra_data.weight}%</div>
                  )}
                  {level2.extra_data?.total && (
                    <div>만점:{level2.extra_data.total}</div>
                  )}
                </td>
                <td className={styles.evalElementsCell}>
                  <textarea
                    className={`${styles.autoResizeTextarea} ${styles.evalTextarea}`}
                    value={finalItemData.elements}
                    onChange={(e) =>
                      handleChange(level2.id, "elements", e.target.value, e)
                    }
                    rows={1}
                  />
                </td>
                <td className={styles.evalCriteriaCell}>
                  <textarea
                    className={`${styles.autoResizeTextarea} ${styles.evalTextarea}`}
                    value={finalItemData.criteria}
                    onChange={(e) =>
                      handleChange(level2.id, "criteria", e.target.value, e)
                    }
                    rows={1}
                  />
                </td>
              </tr>
            );
          } else {
            // Level3가 있는 경우
            const level2RowSpan = level3Items.length;

            return level3Items.flatMap((level3, level3Index) => {
              const finalItemData = evalPlanData[level3.id] || {
                elements: "",
                criteria: "",
              };

              return (
                <tr key={`level3-${level3.id}-${level3Index}`}>
                  {level2Index === 0 && level3Index === 0 && (
                    <td
                      rowSpan={level1RowSpan}
                      colSpan={1}
                      className={styles.evalItemCell}
                    >
                      <div style={{ fontWeight: "500" }}>{level1.content}</div>
                      {level1.extra_data?.weight && (
                        <div>{level1.extra_data.weight}%</div>
                      )}
                      {level1.extra_data?.total && (
                        <div>만점:{level1.extra_data.total}</div>
                      )}
                    </td>
                  )}
                  {level3Index === 0 && (
                    <td
                      rowSpan={level2RowSpan}
                      colSpan={1}
                      className={styles.evalItemCell}
                    >
                      <div style={{ fontWeight: "500" }}>{level2.content}</div>
                      {level2.extra_data?.weight && (
                        <div>{level2.extra_data.weight}%</div>
                      )}
                      {level2.extra_data?.total && (
                        <div>만점:{level2.extra_data.total}</div>
                      )}
                    </td>
                  )}
                  <td className={styles.evalItemCell}>
                    <div style={{ fontWeight: "500" }}>{level3.content}</div>
                    {level3.extra_data?.weight && (
                      <div>{level3.extra_data.weight}%</div>
                    )}
                    {level3.extra_data?.total && (
                      <div>만점:{level3.extra_data.total}</div>
                    )}
                  </td>
                  <td className={styles.evalElementsCell}>
                    <textarea
                      className={`${styles.autoResizeTextarea} ${styles.evalTextarea}`}
                      value={finalItemData.elements}
                      onChange={(e) =>
                        handleChange(level3.id, "elements", e.target.value, e)
                      }
                      rows={1}
                    />
                  </td>
                  <td className={styles.evalCriteriaCell}>
                    <textarea
                      className={`${styles.autoResizeTextarea} ${styles.evalTextarea}`}
                      value={finalItemData.criteria}
                      onChange={(e) =>
                        handleChange(level3.id, "criteria", e.target.value, e)
                      }
                      rows={1}
                    />
                  </td>
                </tr>
              );
            });
          }
        });
      }
    });
  }, [evalItemsData, findItems, findByLevels, evalPlanData, handleChange, t]);

  return (
    <>
      {/* 백드롭 */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.3)",
          zIndex: 10,
        }}
        onClick={onClose}
      />

      {/* Drawer 패널 */}
      <div className={styles.drawerPanel}>
        <div className={styles.drawerContent}>
          {/* 헤더 */}
          <div className={`${styles.drawerHeader} no-print`}>
            <h4 className={styles.drawerTitle}>
              {t("teacher.lessonRelated.evalPlan") || "평가계획서"}
            </h4>
            <div className="d-flex">
              <button
                type="button"
                className={`${styles.footerButton} ${styles.outline} me-2`}
                onClick={handlePrint}
              >
                🖨️ {t("common.labels.print") || "인쇄"}
              </button>
              <button
                className={`${styles.closeButton} no-print`}
                onClick={onClose}
              >
                ✕
              </button>
            </div>
          </div>

          {/* 인쇄 영역 */}
          <div ref={printRef} className={styles.printContent}>
            {/* 인쇄용 제목 */}
            <div className={styles.printHeader}>
              <h2 className={styles.printTitle}>
                {schoolYearText}-{termText}{" "}
                {t("teacher.lessonRelated.evalPlan") || "평가계획서"}
              </h2>
            </div>
            {/* 수업 정보 테이블 */}
            <table className={styles.infoTable}>
              <tbody>
                <tr>
                  <th>
                    {t("teacher.lessonRelated.lessonPlanModal.subjectName") ||
                      "교과명"}
                  </th>
                  <td>{lessonInfo?.subject_name || ""}</td>
                  <th>
                    {t("teacher.lessonRelated.lessonPlanModal.target") ||
                      "대상"}
                  </th>
                  <td>{targetText}</td>
                  <th>
                    {t("teacher.lessonRelated.lessonPlanModal.teacher") ||
                      "담당 교사"}
                  </th>
                  <td>{teachersText || ""}</td>
                </tr>
                <tr>
                  <th>
                    {t("teacher.lessonRelated.lessonPlanModal.category") ||
                      "구분"}
                  </th>
                  <td>
                    {(() => {
                      const category =
                        lessonInfo?.extra_attributes?.subjectCategory;
                      const sort = lessonInfo?.extra_attributes?.subjectSort;
                      if (category && sort) {
                        return `${sort} - ${category}`;
                      }
                      return sort || category || "";
                    })()}
                  </td>
                  <th>
                    {t("teacher.lessonRelated.lessonPlanModal.hours") || "시수"}
                  </th>
                  <td>{lessonInfo?.lesson_hours || ""}</td>
                  <td colSpan={2}></td>
                </tr>
              </tbody>
            </table>

            {/* 평가계획서 테이블 */}
            <table className={styles.evalTable}>
              <thead>
                <tr>
                  <th
                    colSpan={
                      findByLevels(2).length > 0
                        ? findByLevels(3).length > 0
                          ? 3
                          : 2
                        : 1
                    }
                    className={styles.evalItemHeader}
                  >
                    {t("teacher.lessonRelated.evalItem") || "평가항목"}
                  </th>
                  <th className={styles.evalElementsHeader}>
                    {t("teacher.lessonRelated.evalElements") || "평가요소"}
                  </th>
                  <th className={styles.evalCriteriaHeader}>
                    {t("teacher.lessonRelated.evalCriteria") || "평가기준"}
                  </th>
                </tr>
              </thead>
              <tbody>{renderTableRows()}</tbody>
            </table>
          </div>

          {/* 푸터 - 인쇄 시 숨김 */}
          <div className={`${styles.drawerFooter} no-print`}>
            <div className={styles.footerButtons}>
              <button
                type="button"
                className={`${styles.footerButton} ${styles.secondary}`}
                onClick={onClose}
                disabled={isSaving}
              >
                {t("common.labels.cancel") || "취소"}
              </button>
              <button
                type="button"
                className={`${styles.footerButton} ${styles.primary}`}
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving
                  ? (t("common.status.saving") || "저장중") + "..."
                  : `💾 ${t("common.labels.save") || "저장"}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
